import * as React from 'react';
import {observer} from 'mobx-react';
import {Modal, ModalChildStyle} from './modal';
import {em, px} from 'csx';
import {colorBackground, colorSlate, colorSpotify, colorWhite} from '../theme/color';
import {TextNoOrphan} from './text-no-orphan';
import {style} from 'typestyle';
import {PillStyle, PillStyleDefault, PillStyleDisabled} from './pill';
import {SVGIcon} from './svg-icon';

export interface OnboardingSpotifyModalModel {
  show: boolean;
  spotifyLoginURL: string;
  saving: boolean;
}

export interface OnboardingSpotifyModalProps {
  model: OnboardingSpotifyModalModel;
  onRequestClose(): void;
  onSkip(): void;
  i18n?: {
    title: string;
    spotify: string;
    skip: string;
    skipping: string;
    content: string;
    caveat: string;
  }
}

const DEFAULT_I18N = {
  title: 'Link your Spotify account',
  spotify: 'Continue with Spotify',
  skip: 'Skip for now',
  skipping: 'Skipping...',
  content: 'Discover your favourite tracks from Spotify here on Lickd, and personalise track suggestions to match your listening preferences.',
  caveat: 'You can update your notification settings from My Account at any time.'
};

const link = {
  ...PillStyle,
  ...PillStyleDefault,
  margin: '10px 0 0 0',
  padding: '0.6em',
  lineHeight: px(24),
  background: colorSpotify.toString(),
  $nest: {
    '&:hover': {
      color: colorWhite.toString(),
      background: colorSpotify.darken(0.15).toString()
    },
    '& span': {
      flex: 1,
      textAlign: 'center'
    }
  }
};

export const OnboardingSpotifyModal = observer<OnboardingSpotifyModalProps>(
  ({ model, onRequestClose, onSkip, i18n = DEFAULT_I18N }) => (
    <Modal show={model.show} onRequestClose={onRequestClose}>
      {model.show && (
        <div className={styles.container}>
          <h3>
            <TextNoOrphan value={i18n.title}/>
          </h3>
          <p>{i18n.content}</p>
          <p>{i18n.caveat}</p>
          {!model.saving && (
            <a className={styles.linkContinue} href={model.spotifyLoginURL}>
              <SVGIcon.Spotify color={colorWhite.toString()} />
              <span>{i18n.spotify}</span>
            </a>
          )}
          <button className={styles.linkCancel} disabled={model.saving} onClick={onSkip}>
            <span>{model.saving ? i18n.skipping : i18n.skip}</span>
          </button>
        </div>
      )}
    </Modal>
  )
);


const styles = {
  container: style({
    ...ModalChildStyle,
    width: px(420),
    maxWidth: 'calc(100vw - 4rem)',
    background: colorSlate.toString(),
    $nest: {
      '&> h3': {
        color: colorSpotify.toString(),
        fontSize: px(30),
        margin: '0 0 30px 0',
      },
      '& p': {
        color: colorWhite.toString(),
        margin: '5px 0 20px 0',
        lineHeight: em(1.4)
      }
    }
  }),
  linkContinue: style({
    ...link,
    background: colorSpotify.toString(),
    $nest: {
      '&:hover': {
        color: colorWhite.toString(),
        background: colorSpotify.darken(0.15).toString()
      },
      '& span': {
        flex: 1,
        textAlign: 'center'
      }
    }
  }),
  linkCancel: style({
    ...link,
    color: colorSlate.toString(),
    background: colorBackground.toString(),
    $nest: {
      '&:hover': {
        color: colorSlate.toString(),
        background: colorBackground.darken(0.15).toString(),
      },
      '& span': {
        flex: 1,
        textAlign: 'center'
      },
      '&:disabled': {
        ...PillStyleDisabled
      }
    }
  })
};